import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import SavedFilterVisibility from "@mapmycustomers/shared/enum/SavedFilterVisibility";
import { EntityType, EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import useView from "@app/component/view/utils/useView";
import { getFavoriteFilters } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getFavoriteSavedFilters, isSavedFilterUpdateLoading } from "@app/store/savedFilters";
import { updateSavedFilter } from "@app/store/savedFilters/actions";

import AddEditFilterModal from "./components/AddEditFilterModal";
import { MAX_FAVORITE_FILTERS } from "./util/consts";

export interface Props {
  entityType: Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>;
  favoriteFilters: SavedFilter["id"][];
  filter?: SavedFilter;
  loading: boolean;
  onHide: () => void;
  reachedFavoriteFiltersLimit: boolean;
  updateSavedFilter: typeof updateSavedFilter.request;
}

const UpdateFilterModal: React.FC<Props> = ({
  favoriteFilters,
  filter,
  loading,
  onHide,
  reachedFavoriteFiltersLimit,
  updateSavedFilter,
}) => {
  const intl = useIntl();
  const {
    viewState: { columns, viewAs },
  } = useView();

  const handleSave = useCallback(
    (
      name: string,
      visibility: SavedFilterVisibility,
      favorite: boolean,
      teamId: Team["id"][],
      preventUpdateSavedFilter: boolean,
      owner?: User
    ) => {
      if (filter) {
        updateSavedFilter({
          callback: onHide,
          columns,
          favorite,
          filter: { ...filter, name, teamId, user: owner, viewAs, visibility },
          preventUpdateSavedFilter,
        });
      }
    },
    [updateSavedFilter, columns, filter, onHide, viewAs]
  );

  return filter ? (
    <AddEditFilterModal
      filter={filter}
      isFavorite={favoriteFilters.includes(filter.id)}
      loading={loading}
      okButtonText={intl.formatMessage({
        id: "savedFilters.modal.update.button",
        defaultMessage: "Save Changes",
        description: "Saved Filter create modal update button text",
      })}
      onHide={onHide}
      onSave={handleSave}
      reachedFavoriteFiltersLimit={reachedFavoriteFiltersLimit}
      title={intl.formatMessage({
        id: "savedFilters.modal.update.title",
        defaultMessage: "Edit Saved Filter",
        description: "Saved Filter create modal update text",
      })}
    />
  ) : null;
};

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: EntityTypesSupportingSavedFilters }
) => ({
  favoriteFilters: getFavoriteFilters(state),
  loading: isSavedFilterUpdateLoading(state),
  reachedFavoriteFiltersLimit:
    getFavoriteSavedFilters(state)(ownProps.entityType).length >= MAX_FAVORITE_FILTERS,
});

const mapDispatchToProps = {
  updateSavedFilter: updateSavedFilter.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateFilterModal);
